<template>
  <!-- A live tile for the sidebar (2026-09-22). The sidebar lives in the
       host window, which has no magazine CSS, so the tile is a small frame
       with the magazine's stylesheets and the customer's CI inside — the
       same trick the design picker uses. The frame takes no pointer events:
       the tile above it keeps its drag and its click. The hand moving across
       the tile sweeps through the designs; the frame is told which one. -->
  <div
    ref="box"
    class="snippet-live"
    :style="{ height: `${height}px` }"
    @mousemove="sweep"
    @mouseleave="rest"
  >
    <iframe
      v-if="doc"
      ref="frame"
      class="snippet-live-frame"
      :style="frameStyle"
      :srcdoc="doc"
      sandbox="allow-scripts"
      loading="lazy"
      tabindex="-1"
      aria-hidden="true"
      title=""
    />
    <slot v-else />
  </div>
</template>

<script>
import {
  frameIndexAt, previewFrameDocument, previewFrames, previewFrameWidth,
} from '@/editor-v2/utils/livePreviewFrames';
import { getDesignPreviewContext } from './controls/designs/previewContext';

/** A tile is as tall as its row, within reason. */
const MIN_HEIGHT = 72;
const MAX_HEIGHT = 240;

export default {
  name: 'SnippetLivePreview',
  props: {
    html: { type: String, default: '' },
    locale: { type: String, default: 'de' },
  },
  data() {
    // The document is only built once the tile has scrolled into view: a
    // sidebar holds dozens of tiles, and each frame parses the theme CSS.
    // The frame is laid out at full width (media queries read the viewport)
    // and scaled down to the tile; it reports its own height once drawn.
    return {
      seen: false, index: 0, boxWidth: 0, frameHeight: 0,
    };
  },
  computed: {
    frames() {
      return previewFrames(this.html);
    },
    doc() {
      if (!this.seen) return '';
      return previewFrameDocument(this.frames, { ...getDesignPreviewContext(), locale: this.locale });
    },
    frameWidth() {
      return previewFrameWidth(this.frames);
    },
    scale() {
      return this.boxWidth > 0 ? this.boxWidth / this.frameWidth : 0;
    },
    /** The tile is as tall as the scaled frame, within reason. */
    height() {
      const scaled = Math.round(this.frameHeight * this.scale);
      return Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, scaled));
    },
    frameStyle() {
      return {
        width: `${this.frameWidth}px`,
        height: `${Math.max(this.frameHeight, MIN_HEIGHT)}px`,
        transform: `scale(${this.scale})`,
      };
    },
  },
  mounted() {
    this.onMessage = (event) => {
      const win = this.$refs.frame && this.$refs.frame.contentWindow;
      if (!win || event.source !== win) return;
      const data = event.data || {};
      if (data.type !== 'wm-preview-size') return;
      this.frameHeight = Math.round(Number(data.height) || 0);
      this.measure();
    };
    window.addEventListener('message', this.onMessage);
    this.measure();
    if ('IntersectionObserver' in window && this.$refs.box) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.seen = true;
          this.observer.disconnect();
        }
      }, { rootMargin: '200px' });
      this.observer.observe(this.$refs.box);
    } else {
      this.seen = true;
    }
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('message', this.onMessage);
  },
  methods: {
    measure() {
      this.boxWidth = this.$refs.box ? this.$refs.box.clientWidth : 0;
    },
    show(index) {
      if (index === this.index) return;
      this.index = index;
      const win = this.$refs.frame && this.$refs.frame.contentWindow;
      if (win) win.postMessage({ type: 'wm-preview-frame', index }, '*');
    },
    sweep(event) {
      const width = this.$refs.box ? this.$refs.box.clientWidth : 0;
      this.show(frameIndexAt(event.offsetX, width, this.frames.length));
    },
    rest() {
      this.show(0);
    },
  },
};
</script>

<style scoped>
.snippet-live {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #fff;
  transition: height 0.15s ease-out;
}

.snippet-live-frame {
  display: block;
  transform-origin: top left;
  border: 0;
  pointer-events: none;
}
</style>
